/**
 * Population Exposure Analysis — estimates affected population given an
 * event location and severity-scaled blast/impact radius.
 *
 * Uses a pre-processed 0.5° global population density grid. For each event:
 *   1. Map severity to an impact radius
 *   2. Find all grid cells whose centroids fall within that radius
 *   3. Sum population for those cells (proportional overlap for edge cells)
 *   4. Return human-readable estimate
 *
 * The density grid is loaded lazily on first use and cached.
 */

import { haversineDistance } from '@/utils/geo';
import type { Severity } from '@/sources/base-source';

/** Population grid cell: [lat, lng, population]. */
export type PopCell = [number, number, number];

/** Result of a population exposure calculation. */
export interface PopulationExposure {
  /** Estimated affected population count. */
  population: number;
  /** Formatted display string (e.g. "~2.3M people within 50 km"). */
  display: string;
  /** Impact radius used (km). */
  radiusKm: number;
  /** Number of grid cells that contributed. */
  cellCount: number;
}

/** Severity → default impact radius mapping (km). */
const SEVERITY_RADIUS: Record<string, number> = {
  critical: 100,
  high: 50,
  medium: 25,
  low: 10,
  info: 5,
};

/** Grid cell size in degrees (0.5°). */
const CELL_SIZE_DEG = 0.5;

// --- Lazy-loaded grid cache ---
let gridCache: PopCell[] | null = null;
let gridLoadPromise: Promise<PopCell[]> | null = null;

/**
 * Load the population density grid. Fetches the JSON asset once and caches it.
 * Falls back to an empty grid if the data file is unavailable.
 */
async function loadGrid(): Promise<PopCell[]> {
  if (gridCache) return gridCache;
  if (gridLoadPromise) return gridLoadPromise;

  gridLoadPromise = (async () => {
    try {
      const resp = await fetch('/data/worldpop-density.json');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data: PopCell[] = await resp.json();
      gridCache = data;
      console.log(`[Sentinel] Population grid loaded: ${data.length} cells`);
      return data;
    } catch (err) {
      console.warn('[Sentinel] Population grid unavailable, exposure estimates disabled:', err);
      gridCache = [];
      return [];
    }
  })();

  return gridLoadPromise;
}

/**
 * Format a population count for display.
 * e.g. 2_340_000 → "~2.3M", 45_000 → "~45K", 800 → "~800"
 */
function formatPopulation(pop: number): string {
  if (pop >= 1_000_000) {
    return `~${(pop / 1_000_000).toFixed(1)}M`;
  }
  if (pop >= 1_000) {
    return `~${(pop / 1_000).toFixed(0)}K`;
  }
  return `~${Math.round(pop)}`;
}

/**
 * Calculate population exposure for a given location and severity.
 *
 * @param lat Event latitude
 * @param lng Event longitude
 * @param severity Event severity (determines impact radius)
 * @param customRadiusKm Optional override for the impact radius
 */
export async function calculatePopulationExposure(
  lat: number,
  lng: number,
  severity?: Severity,
  customRadiusKm?: number,
): Promise<PopulationExposure> {
  const grid = await loadGrid();

  const radiusKm = customRadiusKm ?? SEVERITY_RADIUS[severity ?? 'info'] ?? 5;

  if (grid.length === 0) {
    return {
      population: 0,
      display: 'Population data unavailable',
      radiusKm,
      cellCount: 0,
    };
  }

  // Quick bounding-box pre-filter: approximate degrees from km at this latitude
  const latDeg = radiusKm / 111.32;
  const lngDeg = radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180));
  const minLat = lat - latDeg;
  const maxLat = lat + latDeg;
  const minLng = lng - lngDeg;
  const maxLng = lng + lngDeg;

  let totalPop = 0;
  let cellCount = 0;

  for (const [cLat, cLng, pop] of grid) {
    // Fast bounding-box reject
    if (cLat < minLat || cLat > maxLat || cLng < minLng || cLng > maxLng) continue;

    const dist = haversineDistance(lat, lng, cLat, cLng);

    if (dist <= radiusKm) {
      // Full cell within radius
      totalPop += pop;
      cellCount++;
    } else if (dist <= radiusKm + CELL_SIZE_DEG * 111.32 * 0.5) {
      // Edge cell — estimate partial overlap proportionally
      const overlap = Math.max(0, 1 - (dist - radiusKm) / (CELL_SIZE_DEG * 111.32));
      totalPop += pop * overlap;
      cellCount++;
    }
  }

  totalPop = Math.round(totalPop);

  const display = totalPop > 0
    ? `${formatPopulation(totalPop)} people within ${radiusKm} km`
    : `<1K people within ${radiusKm} km`;

  return { population: totalPop, display, radiusKm, cellCount };
}

/**
 * Synchronous version that uses cached grid data.
 * Returns null if the grid hasn't loaded yet.
 * Useful for rendering without awaiting.
 */
export function calculatePopulationExposureSync(
  lat: number,
  lng: number,
  severity?: Severity,
  customRadiusKm?: number,
): PopulationExposure | null {
  if (!gridCache || gridCache.length === 0) return null;

  const radiusKm = customRadiusKm ?? SEVERITY_RADIUS[severity ?? 'info'] ?? 5;

  const latDeg = radiusKm / 111.32;
  const lngDeg = radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180));
  const minLat = lat - latDeg;
  const maxLat = lat + latDeg;
  const minLng = lng - lngDeg;
  const maxLng = lng + lngDeg;

  let totalPop = 0;
  let cellCount = 0;

  for (const [cLat, cLng, pop] of gridCache) {
    if (cLat < minLat || cLat > maxLat || cLng < minLng || cLng > maxLng) continue;

    const dist = haversineDistance(lat, lng, cLat, cLng);

    if (dist <= radiusKm) {
      totalPop += pop;
      cellCount++;
    } else if (dist <= radiusKm + CELL_SIZE_DEG * 111.32 * 0.5) {
      const overlap = Math.max(0, 1 - (dist - radiusKm) / (CELL_SIZE_DEG * 111.32));
      totalPop += pop * overlap;
      cellCount++;
    }
  }

  totalPop = Math.round(totalPop);

  const display = totalPop > 0
    ? `${formatPopulation(totalPop)} people within ${radiusKm} km`
    : `<1K people within ${radiusKm} km`;

  return { population: totalPop, display, radiusKm, cellCount };
}

/** Pre-load the population grid (call early in boot). */
export function preloadPopulationGrid(): void {
  loadGrid().catch(() => {});
}
